/**
 * InstallButton — Discreet always-visible PWA install pill.
 *
 * Companion to InstallBanner: the banner shows once with rich copy and can
 * be dismissed forever; this small pill stays at the bottom of the screen
 * as a permanent, low-intrusion fallback so the user can always install
 * Other Side later, even after dismissing the banner.
 *
 * Visual:
 *   • 38px-tall pill, bottom-left of the viewport (so it doesn't collide
 *     with the "Made with Emergent" badge at bottom-right).
 *   • Black translucent (rgba 78%) + 16px backdrop-blur, gold border.
 *   • OS monogram (22px) + small "Installa" label.
 *   • Subtle entrance (slow fade-up, 1.2s delayed so it never feels rushed).
 *
 * Behavior:
 *   • Mobile-only (<768px JS check + CSS @media defense).
 *   • Hidden when running as standalone (already installed).
 *   • Hidden while the InstallBanner itself is on-screen (avoid duplicate
 *     CTAs). Reappears once the banner is dismissed.
 *   • Android: tap triggers the captured `beforeinstallprompt`. If no
 *     prompt is available yet, briefly opens an iOS-like instructions
 *     tooltip explaining the menu path.
 *   • iOS / Safari: tap opens an instruction tooltip ("Tocca Condividi →
 *     Aggiungi a Home"), since iOS has no programmatic install API.
 *   • Persists: hides itself once the user successfully installs (listens
 *     to `appinstalled`).
 */
import { useEffect, useRef, useState, useCallback } from "react";

// One-time cleanup of legacy localStorage keys from earlier versions of this
// component that persisted "installed forever". Without this, users who had
// installed and then uninstalled the PWA would be stuck never seeing the
// install button again. Idempotent — runs once per page load.
function migrateLegacyKeys() {
    try {
        const legacy = ["os_pwa_installed"];
        legacy.forEach((k) => {
            if (localStorage.getItem(k) !== null) localStorage.removeItem(k);
        });
    } catch { /* noop */ }
}

function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

function isMobile() {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
}

function isIOS() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const iPadOS = ua.includes("Macintosh") && navigator.maxTouchPoints > 1;
    return /iphone|ipod|ipad/i.test(ua) || iPadOS;
}

export default function InstallButton() {
    const [shouldRender, setShouldRender] = useState(false);
    const [bannerOpen, setBannerOpen] = useState(false);
    const [tipOpen, setTipOpen] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const tipTimeoutRef = useRef(null);

    useEffect(() => {
        if (typeof window === "undefined") return;
        // Clean up any legacy "installed forever" key so users who
        // uninstalled the PWA can install it again.
        migrateLegacyKeys();
        // Only hide if currently running as installed PWA (live signal —
        // self-corrects when the user uninstalls and reopens via browser).
        if (isStandalone()) return;
        setShouldRender(true);

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => {
            // Do NOT persist this to localStorage — the user might uninstall
            // and want to reinstall later. We hide for this session only.
            setShouldRender(false);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    // Track whether the rich InstallBanner is currently displayed in the DOM
    // so we can hide ourselves while it is shown (avoid duplicate CTAs).
    useEffect(() => {
        if (!shouldRender) return;
        const check = () => {
            const banner = document.querySelector('[data-testid="pwa-install-banner"]');
            setBannerOpen(!!banner);
        };
        check();
        const obs = new MutationObserver(check);
        obs.observe(document.body, { childList: true, subtree: true });
        return () => obs.disconnect();
    }, [shouldRender]);

    const closeTipSoon = useCallback(() => {
        if (tipTimeoutRef.current) clearTimeout(tipTimeoutRef.current);
        tipTimeoutRef.current = setTimeout(() => setTipOpen(false), 6500);
    }, []);

    const handleClick = useCallback(async () => {
        // Native install path (Android / desktop Chrome with prompt available)
        if (deferredPrompt) {
            try {
                deferredPrompt.prompt();
                const choice = await deferredPrompt.userChoice;
                if (choice?.outcome === "accepted") {
                    setShouldRender(false);
                }
            } catch { /* ignore */ }
            setDeferredPrompt(null);
            return;
        }
        // iOS or Android-without-prompt → show inline instruction tooltip
        setTipOpen((v) => !v);
        closeTipSoon();
    }, [deferredPrompt, closeTipSoon]);

    if (!shouldRender || bannerOpen) return null;

    const ios = isIOS();

    return (
        <>
            <style>{`
                @keyframes os-pill-rise {
                    from { transform: translateX(-50%) translateY(48px); opacity: 0; }
                    to   { transform: translateX(-50%) translateY(0);    opacity: 1; }
                }
                @keyframes os-pill-glow {
                    0%, 100% {
                        box-shadow:
                            0 12px 32px rgba(0,0,0,0.55),
                            0 0 0 0.5px rgba(232,192,125,0.30) inset,
                            0 0 0 rgba(232,192,125,0);
                    }
                    50% {
                        box-shadow:
                            0 16px 44px rgba(0,0,0,0.55),
                            0 0 0 0.5px rgba(232,192,125,0.55) inset,
                            0 0 36px rgba(232,192,125,0.45);
                    }
                }
                .os-install-pill {
                    position: fixed;
                    left: 50%;
                    bottom: max(22px, env(safe-area-inset-bottom, 22px));
                    transform: translateX(-50%);
                    z-index: 9997;
                    display: inline-flex;
                    align-items: center;
                    gap: 13px;
                    padding: 12px 30px 12px 12px;
                    height: 68px;
                    border-radius: 34px;
                    background: rgba(8, 6, 5, 0.88);
                    -webkit-backdrop-filter: blur(22px) saturate(160%);
                    backdrop-filter: blur(22px) saturate(160%);
                    border: 1.5px solid rgba(232, 192, 125, 0.65);
                    color: #fbeecf;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    font-size: 18px;
                    font-weight: 600;
                    letter-spacing: 0.025em;
                    cursor: pointer;
                    user-select: none;
                    -webkit-tap-highlight-color: transparent;
                    transition: transform 200ms ease, border-color 220ms ease;
                    animation:
                        os-pill-rise 700ms cubic-bezier(.2,.8,.2,1) 1.0s both,
                        os-pill-glow 3.8s ease-in-out 1.8s infinite;
                }
                .os-install-pill:hover, .os-install-pill:active {
                    transform: translateX(-50%) translateY(-2px);
                    border-color: rgba(232, 192, 125, 0.95);
                }
                .os-install-pill__icon {
                    width: 44px; height: 44px;
                    border-radius: 50%;
                    background: #000;
                    border: 1.5px solid rgba(232,192,125,0.65);
                    display: inline-flex; align-items: center; justify-content: center;
                    overflow: hidden;
                    flex: 0 0 auto;
                    box-shadow: 0 0 16px rgba(232,192,125,0.30);
                }
                .os-install-pill__icon img {
                    width: 92%; height: 92%; object-fit: contain;
                }
                .os-install-pill__label {
                    line-height: 1;
                    color: #f7e8c8;
                    text-shadow: 0 1px 2px rgba(0,0,0,0.4);
                }
                .os-install-tip {
                    position: fixed;
                    left: 50%;
                    transform: translateX(-50%);
                    bottom: calc(max(20px, env(safe-area-inset-bottom, 20px)) + 70px);
                    z-index: 9997;
                    max-width: calc(100vw - 28px);
                    padding: 13px 16px;
                    border-radius: 14px;
                    background: rgba(8, 6, 5, 0.88);
                    -webkit-backdrop-filter: blur(20px) saturate(150%);
                    backdrop-filter: blur(20px) saturate(150%);
                    border: 1px solid rgba(232,192,125,0.30);
                    box-shadow: 0 14px 32px rgba(0,0,0,0.55);
                    color: #f5ecdb;
                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    font-size: 13.5px;
                    line-height: 1.5;
                    display: flex; gap: 10px; align-items: center;
                    animation: os-pill-rise 320ms ease-out both;
                }
                .os-install-tip__share {
                    width: 18px; height: 18px; flex: 0 0 auto;
                    color: #E8C07D;
                }
                @media (min-width: 769px) {
                    .os-install-pill {
                        left: 22px;
                        bottom: max(22px, env(safe-area-inset-bottom, 22px));
                        transform: none;
                        animation:
                            os-pill-rise-desktop 700ms cubic-bezier(.2,.8,.2,1) 1.0s both,
                            os-pill-glow 4.5s ease-in-out 1.8s infinite;
                    }
                    .os-install-pill:hover, .os-install-pill:active {
                        transform: translateY(-2px);
                    }
                    .os-install-tip {
                        left: 22px;
                        transform: none;
                    }
                }
                @keyframes os-pill-rise-desktop {
                    from { transform: translateY(48px); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
            `}</style>
            <button
                type="button"
                className="os-install-pill"
                aria-label="Installa Other Side"
                onClick={handleClick}
                data-testid="pwa-install-pill"
            >
                <span className="os-install-pill__icon" aria-hidden="true">
                    <img src="/brand/pwa-192.png" alt="" />
                </span>
                <span className="os-install-pill__label">Installa app</span>
            </button>
            {tipOpen && (
                <div
                    role="status"
                    className="os-install-tip"
                    data-testid="pwa-install-pill-tip"
                    onClick={() => setTipOpen(false)}
                >
                    <svg className="os-install-tip__share" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 3v12" />
                        <path d="M8 7l4-4 4 4" />
                        <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
                    </svg>
                    <span>
                        {ios
                            ? <>Tocca <strong style={{ color: "#f3e3c2" }}>Condividi</strong> → <strong style={{ color: "#f3e3c2" }}>Aggiungi a Home</strong>.</>
                            : <>Apri il menu <strong style={{ color: "#f3e3c2" }}>⋮</strong> del browser → <strong style={{ color: "#f3e3c2" }}>Installa app</strong> / <strong style={{ color: "#f3e3c2" }}>Aggiungi a Home</strong>.</>
                        }
                    </span>
                </div>
            )}
        </>
    );
}
